/**
 * Shiki syntax highlighter — lazy-loaded singleton.
 *
 * Shiki is ~2MB if fully loaded; we load just the languages we use to keep
 * the bundle reasonable. The highlighter is async-initialised and cached so
 * the first call costs ~200ms and subsequent ones are instant.
 */

import type { Highlighter } from 'shiki';
import { createHighlighter } from 'shiki';

let instance: Promise<Highlighter> | null = null;

const THEMES = ['github-dark-default', 'github-light'] as const;
const LANGS = ['bash', 'json', 'javascript', 'python', 'php'] as const;

export function getHighlighter(): Promise<Highlighter> {
  if (!instance) {
    instance = createHighlighter({
      themes: THEMES.slice(),
      langs: LANGS.slice(),
    });
  }
  return instance;
}

export type ShikiLang = (typeof LANGS)[number];
export type ShikiTheme = (typeof THEMES)[number];

export async function highlight(
  code: string,
  lang: ShikiLang,
  theme: ShikiTheme = 'github-dark-default'
): Promise<string> {
  const hl = await getHighlighter();
  return hl.codeToHtml(code, { lang, theme });
}
